/**
 * @file design-errors.ts
 * @description Ошибки библиотеки дизайнов словами владельца (phase19.16
 * Р1-6а; решения 2026-10-12): коды сервера → фраза реестром (OCP: новый
 * код — строка), 409 «назначен страницам» — со списком страниц из
 * `details`, и ошибка «правки ещё не сохранились» до похода на сервер.
 */

import { ApiError } from "../api-client";
import type { AssignedPagesDetails } from "../api-site-designs";
import { assignedPagesText } from "./design-status";

/** Коды сервера → фраза для человека. */
const DESIGN_ERROR_TEXT: Readonly<Record<string, string>> = {
  site_design_name_taken: "Дизайн с таким названием уже есть — выберите другое.",
  site_design_name_required: "Назовите дизайн — например, «Лето» или «Новогодний».",
  site_design_limit: "Сохранено 20 дизайнов — больше нельзя. Удалите ненужный.",
  site_design_in_use: "Этот дизайн сейчас на сайте — его нельзя удалить.",
  site_design_forbidden: "Дизайны меняет владелец магазина или сотрудник с правом настроек магазина.",
  site_view_version_conflict: "Оформление только что изменил другой сотрудник. Откройте дизайны заново и повторите.",
  site_design_versions_invalid: "Оформление только что изменил другой сотрудник. Откройте дизайны заново и повторите.",
  site_design_brand_version_required: "Оформление только что изменил другой сотрудник. Откройте дизайны заново и повторите.",
  site_design_not_found: "Дизайн уже удалили — список обновлён.",
};

/** Ошибка «черновик изменил кто-то другой» — окно предлагает обновить список. */
export const DESIGN_CONFLICT_CODES: ReadonlySet<string> = new Set([
  "site_view_version_conflict",
  "site_design_versions_invalid",
  "site_design_brand_version_required",
]);

/** Правки оформления или бренда ещё не дошли до сервера — дизайн не трогаем. */
export class DraftNotSavedError extends Error {
  constructor() {
    super("Последние правки оформления ещё не сохранились — дождитесь сохранения или проверьте связь и повторите.");
    this.name = "DraftNotSavedError";
  }
}

/** Подробности 409 «назначен страницам», если сервер их прислал. */
function assignedPages(details: unknown): AssignedPagesDetails | null {
  if (typeof details !== "object" || details === null) return null;
  const { pages, count } = details as Partial<AssignedPagesDetails>;
  if (!Array.isArray(pages) || typeof count !== "number") return null;
  return { pages, count };
}

/** Текст ошибки для окна или уведомления. */
export function designErrorText(err: unknown, fallback: string): string {
  if (err instanceof DraftNotSavedError) return err.message;
  if (!(err instanceof ApiError)) return fallback;
  if (err.code === "site_design_assigned_to_pages") {
    const details = assignedPages(err.details);
    if (details) return assignedPagesText(details.pages, details.count);
  }
  return DESIGN_ERROR_TEXT[err.code] ?? (err.message || fallback);
}

/** Код ошибки сервера (`null` — не ответ API). */
export function designErrorCode(err: unknown): string | null {
  return err instanceof ApiError ? err.code : null;
}
